import { TColumnDef } from '@pnld/components-web'

import {
  ServiceMapFilterOptions,
  ServiceMapFilterParams,
} from '@/api/processing/types'
import { IFilter, TFilter } from '@/components/DataFilter'
import { ServiceMapFields } from '@/types/processing'

// PSV-426: colunas e filtros do Mapa de Atendimento nas abas Tiragem e
// Processamento. Sem mapa, os helpers devolvem O MESMO array recebido: as
// colunas, as chaves de `initialValues` do DataFilter e o payload ficam
// idênticos aos de antes da 426.

const COMPONENT_COLUMN = 'dsComponent'
const COMPONENT_FILTER = 'coComponent'

const serviceMapColumn = <T extends ServiceMapFields>(
  key: keyof ServiceMapFields,
  header: string
): TColumnDef<T> => ({
  accessorKey: key,
  header,
  enableSorting: false,
  cell: ({ row }) => row.original[key] || '-',
})

// Categoria e Área imediatamente antes de Componente, Etapas Atendidas logo
// depois (hierarquia do mapa). Sem a coluna Componente, nada muda.
export const withServiceMapColumns = <T extends ServiceMapFields>(
  columns: Array<TColumnDef<T>>,
  hasServiceMap: boolean
): Array<TColumnDef<T>> => {
  const anchor = columns.findIndex(
    column => column.accessorKey === COMPONENT_COLUMN
  )
  if (!hasServiceMap || anchor < 0) {
    return columns
  }

  return [
    ...columns.slice(0, anchor),
    serviceMapColumn<T>('dsCategory', 'Categoria'),
    serviceMapColumn<T>('dsArea', 'Área'),
    columns[anchor],
    serviceMapColumn<T>('dsServedStages', 'Etapas Atendidas'),
    ...columns.slice(anchor + 1),
  ]
}

const textSelect = (
  name: keyof ServiceMapFilterParams,
  placeholder: string,
  values?: string[] | null
): IFilter => ({
  type: 'select',
  name,
  placeholder,
  options: (values ?? []).map(value => ({ label: value, value })),
})

// Com mapa, o Componente é filtrado pelo texto do mapa (`dsComponent`): parte
// dos agrupamentos não tem componente em `tb_componente`, e o filtro por id
// não os alcançaria. Os selects existem mesmo sem opções, para que um refetch
// das opções não mude as chaves do formulário (o que o resetaria).
export const serviceMapFilters = (
  filters: Array<IFilter>,
  data: ServiceMapFilterOptions | undefined,
  hasServiceMap: boolean
): Array<IFilter> => {
  const anchor = filters.findIndex(filter => filter.name === COMPONENT_FILTER)
  if (!hasServiceMap || anchor < 0) {
    return filters
  }

  return [
    ...filters.slice(0, anchor),
    textSelect('dsCategory', 'Categoria', data?.dsCategories),
    textSelect('dsArea', 'Área', data?.dsAreas),
    textSelect('dsComponent', 'Componente', data?.dsComponents),
    ...filters.slice(anchor + 1),
  ]
}

// Só as chaves preenchidas: sem mapa (ou com os selects vazios) o spread não
// acrescenta nada ao payload.
export const serviceMapFilterState = (
  filter: TFilter
): ServiceMapFilterParams => {
  const state: ServiceMapFilterParams = {}
  if (filter.dsCategory) state.dsCategory = filter.dsCategory
  if (filter.dsArea) state.dsArea = filter.dsArea
  if (filter.dsComponent) state.dsComponent = filter.dsComponent
  return state
}
